import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { OutpaveCssVars, ToastHost } from "@outpave/ds";
import { useBrandingContext } from "../context/BrandingContext";
import { appKeyForRoute } from "../utils/apps";
import { resolveBrandIdentity } from "./brandIdentity";
import { buildOutpaveTheme } from "./outpaveTheme";
import { useThemePreset } from "./themePresetStore";

/**
 * The DS theme for this company, rebuilt when branding resolves or the
 * language changes.
 *
 * The platform shell deliberately ignores company branding and wears the
 * platform's own: `/platform` administers the platform, so its colour says
 * WHICH platform you are in, not which company you happen to belong to. A
 * platform owner whose product is purple wants every admin visiting it to see
 * purple, including one whose own company is red, precisely so nobody mistakes
 * the operator's console for their own workspace. Everywhere else, company
 * wins per knob (D1).
 */
export const useDsTheme = () => {
  const { brandColors, platform, isBrandingLoaded } = useBrandingContext();
  const { pathname } = useLocation();
  const isPlatformApp = appKeyForRoute(pathname) === "platform";
  const preset = useThemePreset();
  const { i18n } = useTranslation();
  const { language } = i18n;

  return useMemo(() => {
    if (!isBrandingLoaded)
      return buildOutpaveTheme(undefined, preset, language);
    const identity = resolveBrandIdentity(
      isPlatformApp ? null : brandColors,
      platform?.brandColors,
    );
    return buildOutpaveTheme(identity, preset, language);
  }, [
    isBrandingLoaded,
    isPlatformApp,
    brandColors,
    platform?.brandColors,
    preset,
    language,
  ]);
};

/**
 * The app's base theme, the single :root token writer, and the one ToastHost
 * that renders every `toast.*` call. Must mount inside BrandingProvider and
 * outside the legacy theme providers.
 */
export const DsRootTheme: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useDsTheme();
  const { t } = useTranslation();

  return (
    // MUI v9 owns data-theme and persists mode itself; without modeStorageKey
    // it uses "mui-mode" and re-stamps the theme's dark default every boot.
    <ThemeProvider
      theme={theme}
      defaultMode="system"
      modeStorageKey="outpave-color-scheme"
    >
      <OutpaveCssVars />
      <ToastHost labels={{ region: t("Notifications"), close: t("Close") }} />
      {children}
    </ThemeProvider>
  );
};
